/**
 * @file       verification component
 * @version    2.0.0
 * @author     Boris Veits
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or 
 *             altered in anyway without expressed written permission from 
 *             MindShare HDV LLC.
 */

import { Component, ElementRef, ViewChildren, QueryList, signal } 
    from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, ReactiveFormsModule } 
    from '@angular/forms';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { AuthService } from '@app/service/auth.service';
import { HomepageService } from '@app/service/homepage.service';
import { SettingService } from '@app/service/setting.service';
  
@Component
({
    selector: 'app-verify',
    templateUrl: './verify.component.html',
    styleUrls: ['./verify.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatButtonModule
    ]
})

/**
 * @desc   - Verification Compoment
 */  
class VerifyComponent 
{
    @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;

    private subscriptions = [];

    verifyForm: FormGroup;
    loading = signal(false);
    isSubmitted = signal(false);
    attempts = signal(0);
    codeToVerify = signal('');
    error = signal('');

    //_____________________________ C O N S T R U C T O R ______________________ 
    /**
     * @desc   - component constructor
     * @access - 
     * @param  - fb: FormBuilder,
     *         - router: Router
     *                  - authService: AuthService
         *         - homepageService: HomepageService
         *         - settingService: SettingService
         * @return - 
     */ 
    constructor
    (
        private fb: FormBuilder,
        private router: Router,
        private authService: AuthService,
        private homepageService: HomepageService,
        private settingService: SettingService
    )
    {}

    //______________________ S T A T I C  M E T H O D S ________________________
    /**
     * @desc   - gets the form controls for the verify code
     * @access - 
     * @param  - 
     * @return - return the form controls for the verify code
     */  
    get verifyCodeControls() 
    {
        return (this.verifyForm.get('verifyCode') as FormArray).controls;
    }

    ngOnInit(): void
    {
        let hasUser: boolean;
        
        this.verifyForm = this.fb.group(
        {
            verifyCode: this.fb.array([...Array(7)].map(() => 
                this.fb.control('', [Validators.required])))
        });
        hasUser = this.authService.userValue ? true : false;

        if (!hasUser)
        {
            this.router.navigate(['/login']);
        }
        else
        {
            void this.initializeVerificationFlow();
        }
    }

    /**
     * @desc   - load settings then route or request MFA code
     * @access - private
     * @param  -
     * @return -
     */
    private async initializeVerificationFlow()
    {
        let subscription: Subscription;
        let requiresVerification: boolean;
        let isSent: boolean;
        let shouldSkipVerification: boolean;
        let isGlobalMfaEnabled: boolean;
        let responseData: any;

        await this.settingService.loadSettings(true);
        requiresVerification = this.authService.requiresVerification();
        isGlobalMfaEnabled = this.settingService.isMultiFactorEnabled();

        if (!requiresVerification || !isGlobalMfaEnabled)
        {
            this.authService.setVerifiedStatus(true);
            this.homepageService
            .homepageProccessing(this.authService.userValue.uId);
        }
        else
        {
            this.authService.setVerifiedStatus(false);
            subscription = this.authService.generateCode()
            .subscribe(
            {
                next: (data: any) =>
                {
                    responseData = data?.cyData ? data.cyData : data;
                    isSent = responseData?.sent === true;
                    shouldSkipVerification =
                        responseData?.skipVerification === true;
                    if (!isSent && shouldSkipVerification)
                    {
                        this.authService.setVerifiedStatus(true);
                        this.homepageService
                        .homepageProccessing(
                            this.authService.userValue.uId
                        );
                    }
                    else if (!isSent)
                    {
                        this.error.set(
                            'Verification code could not be sent.'
                        );
                    }
                },
                error: (error) =>
                {
                    console.error('Error generating code:', error);
                }
            });
    
            this.subscriptions.push(subscription);
        }
    }

    /**
     * @desc   - angular function when the page is closing
     * @access - public
     * @param  - 
     * @return - 
     */            
    ngOnDestroy(): void
    {
        let i: number;
        
        for (i=0;i<this.subscriptions.length;i++)
        {
            this.subscriptions[i].unsubscribe();
        }
        
        this.subscriptions = [];
    }

    //___________________ P U B L I C  M E T H O D S ___________________________

    /**
     * @desc   - handles the form fields and handles it to submit the form
     * @access - public
     * @param  - event: Event
     *         - index: number
     * @return - 
     */   
    public handleInput
    (
        event: Event, 
        index: number
    ): void 
    {
        let input: HTMLInputElement;;
        let value: string;

        input = event.target as HTMLInputElement;
        value = input.value;
      
        value = value.replace(/\D/g, '');
      
        if (value.length > 1) 
        {
            value = value.charAt(0);
        }
      
        input.value = value;
      
        if (value && index < 6) 
        {
            this.otpInputs.get(index + 1)?.nativeElement.focus();
        }
      
        if (index === 6 && value) 
        {
            this.onSubmit();
        }
    }

    /**
     * @desc   - paste full code into OTP fields
     * @access - public
     * @param  - event: ClipboardEvent
     * @return -
     */
    public handlePaste
    (
        event: ClipboardEvent
    ): void
    {
        let pastedText: string;
        let numericCode: string;
        let i: number;
        let codeArray: FormArray;

        pastedText = event.clipboardData?.getData('text') || '';
        numericCode = pastedText.replace(/\D/g, '').slice(0, 7);
        codeArray = this.verifyForm.get('verifyCode') as FormArray;

        if (numericCode.length > 0)
        {
            event.preventDefault();
            for (i = 0; i < 7; i++)
            {
                codeArray.at(i).setValue(numericCode.charAt(i) || '');
            }

            if (numericCode.length === 7)
            {
                this.onSubmit();
            }
        }
    }
      
    
    /**
     * @desc   - submit the form
     * @access - public
     * @param  - 
     * @return - 
     */  
    onSubmit(): void 
    {
        let code: string;
        let subscription: Subscription;
        let requiresVerification: boolean;
        let shouldProceed: boolean;

        this.loading.set(true);
    
        code = this.verifyForm.value.verifyCode.join('');
        requiresVerification = this.authService.requiresVerification();
        shouldProceed = true;

        if (!requiresVerification)
        {
            this.loading.set(false);
            this.router.navigate(['/login']);
            shouldProceed = false;
        }

        if (shouldProceed)
        {
            this.attempts.update((count) => count + 1);
        
            subscription = this.authService.verifyCode(code)
            .subscribe(
            {
                next: (data) =>
                {
                    if (data.verified)
                    {
                        this.authService.setVerifiedStatus(true);
                        this.homepageService
                        .homepageProccessing(this.authService.userValue.uId);
                    }
                    else 
                    {
                        this.error.set('Invalid verification code');
                        
                        if (this.attempts() >= 3) 
                        {
                            this.router.navigate(['/login']);
                        }
                    }
                    this.loading.set(false);
                },
                error: (error) =>
                {
                    console.error('Error verifying code:', error);
                    this.loading.set(false);
                }
            });

            this.subscriptions.push(subscription);
        }
    }
    
    /**
     * @desc   - reset the form
     * @access - public
     * @param  - 
     * @return - 
     */  
    resetCode(): void
    {
        this.verifyForm.reset();
    }
}

export { VerifyComponent };
